import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import SubscriptionCheckout from '@/components/SubscriptionCheckout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Check, ArrowRight, Heart, Sparkles, Users, Crown, Palmtree, ArrowLeft, Quote, X, Calendar, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Transformation-focused pathway data
const PATHWAY_TRANSFORMATIONS = {
  inner_circle: {
    identity: "The woman who finally puts herself first",
    tagline: "Stop carrying the world. Start coming home to yourself.",
    forTheWomanWho: [
      "is tired of being everyone's rock while crumbling inside",
      "knows she deserves more but keeps postponing her own needs",
      "wants sisterhood that actually sees and holds her",
      "is ready to stop shrinking and start showing up"
    ],
    transformation: {
      before: [
        "Carrying everyone else's weight",
        "Second-guessing every decision",
        "Feeling invisible in her own life",
        "Running on empty, always depleted"
      ],
      after: [
        "Holding space for her own needs first",
        "Moving with quiet, unshakeable confidence",
        "Seen, celebrated, and deeply understood",
        "Nourished by sisterhood and sacred rest"
      ]
    },
    testimonial: {
      quote: "Inner Circle gave me permission to finally put myself first. For the first time in years, I'm not apologizing for taking up space.",
      author: "Michelle T.",
      role: "Inner Circle Member, 8 months"
    },
    image: "https://customer-assets.emergentagent.com/job_4404ff07-d4d6-4eff-9440-36088e898eba/artifacts/idm3t3ed_self%20first%205.jpeg"
  },
  inner_edge: {
    identity: "The leader who commands every room she enters",
    tagline: "Discover your edge. Own your presence. Lead without apology.",
    forTheWomanWho: [
      "has been told she's 'too much' or 'not enough'",
      "is ready to stop dimming her light for others' comfort",
      "wants to lead with both power AND grace",
      "knows there's another level but can't quite reach it alone"
    ],
    transformation: {
      before: [
        "Doubting her leadership presence",
        "Overthinking how she's perceived",
        "Playing small to avoid conflict",
        "Exhausted from code-switching"
      ],
      after: [
        "Walking into rooms with magnetic authority",
        "Trusting her instincts completely",
        "Setting boundaries without guilt",
        "Leading authentically in her full power"
      ]
    },
    testimonial: {
      quote: "The Inner Edge Diagnostic showed me blind spots I'd been carrying for years. Now I walk into boardrooms differently. They feel the shift.",
      author: "Keisha R.",
      role: "VP of Operations, Inner Edge Graduate"
    },
    image: "https://images.unsplash.com/photo-1707995673778-f7ded37a4c17?w=800"
  },
  coaching_activation: {
    identity: "The woman who trusts her own voice again",
    tagline: "Deep, personalized transformation with a coach who truly sees you.",
    forTheWomanWho: [
      "has lost touch with who she really is",
      "needs a thinking partner, not just advice",
      "is ready for breakthroughs, not Band-Aids",
      "wants someone to hold her accountable to her highest self"
    ],
    transformation: {
      before: [
        "Disconnected from her inner wisdom",
        "Stuck in patterns that no longer serve",
        "Afraid to ask for what she really wants",
        "Living someone else's version of success"
      ],
      after: [
        "Deeply connected to her truth and intuition",
        "Breaking generational cycles with clarity",
        "Asking boldly and receiving abundantly",
        "Creating a life that actually fits her"
      ]
    },
    testimonial: {
      quote: "My coach didn't just help me reach my goals—she helped me remember who I was before the world told me who to be. That's priceless.",
      author: "Danielle M.",
      role: "Signature Coaching Client"
    },
    image: "https://images.unsplash.com/photo-1573496267478-37727ee5b694?w=800"
  },
  sdc: {
    identity: "The woman who has arrived—and brings others with her",
    tagline: "The ultimate container for women who are ready to lead legacies.",
    forTheWomanWho: [
      "is no longer climbing—she's building",
      "wants access, not just content",
      "is ready for unlimited support at the highest level",
      "knows her next chapter requires a different table"
    ],
    transformation: {
      before: [
        "Successful but isolated at the top",
        "Craving deep connection with equals",
        "Limited by transactional relationships",
        "Wanting more but unsure of the next level"
      ],
      after: [
        "Surrounded by a lifetime sisterhood of powerhouses",
        "Connected to women who open doors",
        "Supported with unlimited coaching and concierge care",
        "Building legacy, not just career"
      ]
    },
    testimonial: {
      quote: "SDC isn't a program—it's a family. The introductions alone have changed the trajectory of my business. This is where moguls are made.",
      author: "Dr. Simone J.",
      role: "SDC Member, Founder & CEO"
    },
    image: "https://customer-assets.emergentagent.com/job_7ae042af-d5fd-4d89-854a-3356c8c74821/artifacts/utah8q1b_SDC6.jpg"
  },
  costa_rica: {
    identity: "The woman who remembers who she was before the world told her who to be",
    tagline: "Seven days to release, restore, and return to yourself.",
    forTheWomanWho: [
      "can't remember the last time she truly exhaled",
      "needs distance from her daily life to hear herself think",
      "is ready to heal, not just escape",
      "wants transformation surrounded by sisters, not strangers"
    ],
    transformation: {
      before: [
        "Burnt out and running on fumes",
        "Disconnected from her body and spirit",
        "Too busy to process her own healing",
        "Craving stillness but afraid to stop"
      ],
      after: [
        "Restored, rested, and deeply nourished",
        "Reconnected to her sensuality and joy",
        "Integrated and clear on her next chapter",
        "Bonded with sisters for life"
      ]
    },
    testimonial: {
      quote: "I went to Costa Rica exhausted and came back transformed. The jungle, the sisterhood, the healing—it changed something in my DNA.",
      author: "Tanya W.",
      role: "Just Be Costa Rica Alumna"
    },
    image: "https://images.unsplash.com/photo-1640797881028-707994ea430b?w=800"
  }
};

// How did you hear about us options
const REFERRAL_SOURCES = [
  "Social Media (Instagram, Facebook, LinkedIn)",
  "Friend or Family Referral",
  "Podcast",
  "Speaking Event or Conference",
  "Google Search",
  "Current or Former Member",
  "Other"
];

const PathwaysPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [pathways, setPathways] = useState(null);
  const [tiers, setTiers] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);
  
  // Modal states
  const [innerEdgeModalOpen, setInnerEdgeModalOpen] = useState(false);
  const [sdcModalOpen, setSdcModalOpen] = useState(false);
  const [coachingModalOpen, setCoachingModalOpen] = useState(false);
  const [showCalendly, setShowCalendly] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Form states
  const [innerEdgeForm, setInnerEdgeForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    referralSource: '',
    referralOther: ''
  });
  
  const [sdcForm, setSdcForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    title: '',
    interest: '',
    goals: ''
  });
  
  const [coachingForm, setCoachingForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    currentChallenge: '',
    desiredOutcome: '',
    timeline: ''
  });

  useEffect(() => {
    fetchPathways();
    fetchTiers();
  }, []);

  const fetchPathways = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/pathways`);
      setPathways(response.data);
    } catch (error) {
      console.error('Failed to fetch pathways:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTiers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/subscription/tiers`);
      setTiers(response.data.tiers);
    } catch (error) {
      console.error('Failed to fetch tiers:', error);
    }
  };

  const handleStartTrial = (tierId) => {
    if (isAuthenticated && user?.active_tiers?.includes(tierId)) {
      toast.info('You already have access to this tier');
      navigate('/dashboard');
      return;
    }
    
    if (tierId === 'inner_circle' && !isAuthenticated) {
      navigate('/inner-circle');
      return;
    }
    
    if (!isAuthenticated) {
      toast.info('Please create an account first');
      navigate('/register', { state: { selectedTier: tierId } });
      return;
    }
    
    setSelectedTier(tierId);
    setCheckoutOpen(true);
  };

  // Submit Inner Edge Application
  const handleInnerEdgeSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      await axios.post(`${API_URL}/api/inquiries/inner-edge`, {
        ...innerEdgeForm,
        referralSource: innerEdgeForm.referralSource === 'Other' 
          ? innerEdgeForm.referralOther 
          : innerEdgeForm.referralSource
      });
      
      toast.success('Application received! Schedule your call below.');
      setShowCalendly(true);
    } catch (error) {
      toast.error('Failed to submit application. Please try again.');
      console.error('Inner Edge submission error:', error);
    } finally {
      setSubmitting(false);
    }
  };

  // Submit SDC Invitation Request
  const handleSdcSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      await axios.post(`${API_URL}/api/inquiries/sdc`, sdcForm);
      
      toast.success('Your invitation request has been sent to Crystal and Dr. Nicole. They will be in touch soon!');
      setSdcModalOpen(false);
      setSdcForm({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        company: '',
        title: '',
        interest: '',
        goals: ''
      });
    } catch (error) {
      toast.error('Failed to submit request. Please try again.');
      console.error('SDC submission error:', error);
    } finally {
      setSubmitting(false);
    }
  };

  // Submit Coaching Interest
  const handleCoachingSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      await axios.post(`${API_URL}/api/inquiries/coaching`, coachingForm);
      
      toast.success('Thank you for your interest! Our team will reach out to discuss your coaching journey.');
      setCoachingModalOpen(false);
      setCoachingForm({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        currentChallenge: '',
        desiredOutcome: '',
        timeline: ''
      });
    } catch (error) {
      toast.error('Failed to submit request. Please try again.');
      console.error('Coaching submission error:', error);
    } finally {
      setSubmitting(false);
    }
  };

  // Transformation Card Component
  const TransformationCard = ({ before, after }) => (
    <div className="grid md:grid-cols-2 gap-4 my-6">
      <div className="bg-gray-100 rounded-xl p-5">
        <p className="text-xs uppercase tracking-wide text-gray-500 mb-3 font-medium">Where you are now</p>
        <ul className="space-y-2">
          {before.map((item, i) => (
            <li key={i} className="text-gray-600 text-sm flex items-start gap-2">
              <span className="text-gray-400 mt-0.5">—</span>
              {item}
            </li>
          ))}
        </ul>
      </div>
      <div className="bg-gradient-to-br from-forest/10 to-terracotta/10 rounded-xl p-5">
        <p className="text-xs uppercase tracking-wide text-terracotta mb-3 font-medium">Who you become</p>
        <ul className="space-y-2">
          {after.map((item, i) => (
            <li key={i} className="text-forest text-sm flex items-start gap-2 font-medium">
              <Sparkles className="h-4 w-4 text-terracotta flex-shrink-0 mt-0.5" />
              {item}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );

  // Testimonial Component
  const TestimonialQuote = ({ testimonial }) => (
    <div className="bg-sand/30 rounded-xl p-5 my-6 relative">
      <Quote className="h-8 w-8 text-terracotta/30 absolute top-4 left-4" />
      <p className="text-gray-700 italic pl-8 mb-3">"{testimonial.quote}"</p>
      <div className="pl-8">
        <p className="font-medium text-forest">{testimonial.author}</p>
        <p className="text-sm text-gray-500">{testimonial.role}</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-cream">
      <Navbar />
      
      {/* Back to Home */}
      <div className="bg-forest pt-20 pb-2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/" className="inline-flex items-center gap-2 text-white/80 hover:text-white transition-colors">
            <ArrowLeft className="h-4 w-4" />
            <span className="font-medium">Back to Home</span>
          </Link>
        </div>
      </div>

      {/* Hero Section */}
      <section className="bg-forest pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge className="bg-terracotta/20 text-white border-terracotta mb-6">Your Transformation Awaits</Badge>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            Which Woman Are You Becoming?
          </h1>
          <p className="text-xl text-white/80 max-w-2xl mx-auto leading-relaxed">
            Each pathway is designed not just to give you tools—but to help you become the woman you were always meant to be.
          </p>
        </div>
      </section>

      {/* Inner Circle - Entry Point */}
      <section className="py-20 bg-white" data-testid="pathway-inner-circle">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Image */}
            <div className="relative">
              <img 
                src={PATHWAY_TRANSFORMATIONS.inner_circle.image}
                alt="Women in sisterhood"
                className="rounded-2xl shadow-2xl w-full h-[400px] object-cover object-center"
              />
              <Badge className="absolute top-4 left-4 bg-terracotta text-white">Start Here</Badge>
            </div>
            
            {/* Content */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="h-12 w-12 rounded-xl bg-terracotta flex items-center justify-center">
                  <Heart className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h2 className="font-heading text-2xl font-bold text-forest">Inner Circle</h2>
                  <p className="text-terracotta font-bold">$97/month</p>
                </div>
              </div>
              
              <h3 className="font-heading text-3xl md:text-4xl text-forest mb-3">
                {PATHWAY_TRANSFORMATIONS.inner_circle.identity}
              </h3>
              <p className="text-lg text-gray-600 mb-6">
                {PATHWAY_TRANSFORMATIONS.inner_circle.tagline}
              </p>
              
              {/* For the woman who... */}
              <div className="mb-6">
                <p className="text-sm uppercase tracking-wide text-terracotta font-medium mb-3">This is for the woman who...</p>
                <ul className="space-y-2">
                  {PATHWAY_TRANSFORMATIONS.inner_circle.forTheWomanWho.map((item, i) => (
                    <li key={i} className="text-gray-700 flex items-start gap-2">
                      <span className="text-terracotta">...</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              
              <TransformationCard 
                before={PATHWAY_TRANSFORMATIONS.inner_circle.transformation.before}
                after={PATHWAY_TRANSFORMATIONS.inner_circle.transformation.after}
              />
              
              <TestimonialQuote testimonial={PATHWAY_TRANSFORMATIONS.inner_circle.testimonial} />
              
              {/* What's Included - Collapsible */}
              <details className="mb-6">
                <summary className="cursor-pointer text-forest font-medium hover:text-terracotta transition-colors">
                  What's included in Inner Circle →
                </summary>
                <div className="mt-4 grid sm:grid-cols-2 gap-2 pl-4">
                  {[
                    "Weekly Gems (audio/text)",
                    "Weekly Reflection Prompts",
                    "Monthly Live Calls + Replays",
                    "Community Feed Access",
                    "Curated Library",
                    "Basic Directory Access",
                    "Pathways to Deeper Work"
                  ].map((feature, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-terracotta flex-shrink-0" />
                      <span className="text-gray-600 text-sm">{feature}</span>
                    </div>
                  ))}
                </div>
              </details>
              
              <Button 
                className="btn-secondary h-14 px-8 text-lg"
                onClick={() => handleStartTrial('inner_circle')}
                data-testid="join-inner-circle-btn"
              >
                Begin Your Transformation <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Premium Pathways Header */}
      <section className="py-12 bg-sand/30">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-forest mb-4">
            Ready for Deeper Transformation?
          </h2>
          <p className="text-lg text-gray-600">
            These pathways are for women ready to go all in on becoming who they were created to be.
          </p>
        </div>
      </section>

      {/* Inner Edge */}
      <section className="py-20 bg-white" data-testid="pathway-inner-edge">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Content */}
            <div className="order-2 lg:order-1">
              <div className="flex items-center gap-3 mb-4">
                <div className="h-12 w-12 rounded-xl bg-forest flex items-center justify-center">
                  <Sparkles className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h2 className="font-heading text-2xl font-bold text-forest">Inner Edge</h2>
                  <p className="text-terracotta font-bold">$5,000 <span className="text-gray-400 font-normal text-sm">one-time</span></p>
                </div>
              </div>
              
              <h3 className="font-heading text-3xl md:text-4xl text-forest mb-3">
                {PATHWAY_TRANSFORMATIONS.inner_edge.identity}
              </h3>
              <p className="text-lg text-gray-600 mb-6">
                {PATHWAY_TRANSFORMATIONS.inner_edge.tagline}
              </p>
              
              <div className="mb-6">
                <p className="text-sm uppercase tracking-wide text-forest font-medium mb-3">This is for the woman who...</p>
                <ul className="space-y-2">
                  {PATHWAY_TRANSFORMATIONS.inner_edge.forTheWomanWho.map((item, i) => (
                    <li key={i} className="text-gray-700 flex items-start gap-2">
                      <span className="text-forest">...</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              
              <TransformationCard 
                before={PATHWAY_TRANSFORMATIONS.inner_edge.transformation.before}
                after={PATHWAY_TRANSFORMATIONS.inner_edge.transformation.after}
              />
              
              <TestimonialQuote testimonial={PATHWAY_TRANSFORMATIONS.inner_edge.testimonial} />
              
              <details className="mb-6">
                <summary className="cursor-pointer text-forest font-medium hover:text-terracotta transition-colors">
                  What's included in Inner Edge →
                </summary>
                <div className="mt-4 space-y-2 pl-4">
                  <div className="flex items-center gap-2"><Check className="h-4 w-4 text-forest" /><span className="text-gray-600 text-sm">Everything in Inner Circle</span></div>
                  <div className="flex items-center gap-2"><Check className="h-4 w-4 text-forest" /><span className="text-gray-600 text-sm">Inner Edge Diagnostic + Results</span></div>
                  <div className="flex items-center gap-2"><Check className="h-4 w-4 text-forest" /><span className="text-gray-600 text-sm">4 Power & Presence Circles</span></div>
                  <div className="flex items-center gap-2"><Check className="h-4 w-4 text-forest" /><span className="text-gray-600 text-sm">4 Coach Sessions</span></div>
                  <div className="flex items-center gap-2"><Check className="h-4 w-4 text-forest" /><span className="text-gray-600 text-sm">C-Sweet Consultation</span></div>
                </div>
              </details>
              
              <Button 
                className="btn-primary h-14 px-8 text-lg"
                onClick={() => navigate('/the-inner-edge')}
                data-testid="apply-inner-edge-btn"
              >
                Apply for Inner Edge <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
            
            {/* Image */}
            <div className="order-1 lg:order-2">
              <img 
                src={PATHWAY_TRANSFORMATIONS.inner_edge.image}
                alt="Confident woman leader"
                className="rounded-2xl shadow-2xl w-full h-[450px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Signature Coaching */}
      <section className="py-20 bg-cream" data-testid="pathway-coaching">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Image */}
            <div>
              <img 
                src={PATHWAY_TRANSFORMATIONS.coaching_activation.image}
                alt="Woman in coaching session"
                className="rounded-2xl shadow-2xl w-full h-[450px] object-cover"
              />
            </div>
            
            {/* Content */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="h-12 w-12 rounded-xl bg-forest flex items-center justify-center">
                  <Users className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h2 className="font-heading text-2xl font-bold text-forest">Signature Coaching</h2>
                  <p className="text-terracotta font-bold">$8,000 <span className="text-gray-400 font-normal text-sm">one-time</span></p>
                </div>
              </div>
              
              <h3 className="font-heading text-3xl md:text-4xl text-forest mb-3">
                {PATHWAY_TRANSFORMATIONS.coaching_activation.identity}
              </h3>
              <p className="text-lg text-gray-600 mb-6">
                {PATHWAY_TRANSFORMATIONS.coaching_activation.tagline}
              </p>
              
              <div className="mb-6">
                <p className="text-sm uppercase tracking-wide text-forest font-medium mb-3">This is for the woman who...</p>
                <ul className="space-y-2">
                  {PATHWAY_TRANSFORMATIONS.coaching_activation.forTheWomanWho.map((item, i) => (
                    <li key={i} className="text-gray-700 flex items-start gap-2">
                      <span className="text-forest">...</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              
              <TransformationCard 
                before={PATHWAY_TRANSFORMATIONS.coaching_activation.transformation.before}
                after={PATHWAY_TRANSFORMATIONS.coaching_activation.transformation.after}
              />
              
              <TestimonialQuote testimonial={PATHWAY_TRANSFORMATIONS.coaching_activation.testimonial} />
              
              {/* Session Wallet Highlight */}
              <div className="bg-forest/5 rounded-xl p-5 mb-6">
                <p className="font-medium text-forest mb-3">Your Session Wallet Includes:</p>
                <div className="flex flex-wrap gap-3">
                  <span className="bg-forest text-white px-4 py-2 rounded-full text-sm font-medium">9 Coach Sessions</span>
                  <span className="bg-terracotta text-white px-4 py-2 rounded-full text-sm font-medium">3 Wonder Twin Sessions</span>
                </div>
                <p className="text-sm text-gray-500 mt-3">With Crystal & Dr. Nicole personally</p>
              </div>
              
              <Button 
                className="btn-primary h-14 px-8 text-lg"
                onClick={() => setCoachingModalOpen(true)}
                data-testid="begin-coaching-btn"
              >
                Begin Your Journey <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Costa Rica Retreat */}
      <section className="py-20 bg-white" data-testid="pathway-costa-rica">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Content */}
            <div className="order-2 lg:order-1">
              <div className="flex items-center gap-3 mb-4">
                <div className="h-12 w-12 rounded-xl bg-terracotta flex items-center justify-center">
                  <Palmtree className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h2 className="font-heading text-2xl font-bold text-forest">Just Be Costa Rica</h2>
                  <p className="text-terracotta font-bold">From $4,997</p>
                </div>
              </div>
              
              <h3 className="font-heading text-3xl md:text-4xl text-forest mb-3">
                {PATHWAY_TRANSFORMATIONS.costa_rica.identity}
              </h3>
              <p className="text-lg text-gray-600 mb-6">
                {PATHWAY_TRANSFORMATIONS.costa_rica.tagline}
              </p>
              
              <div className="mb-6">
                <p className="text-sm uppercase tracking-wide text-terracotta font-medium mb-3">This is for the woman who...</p>
                <ul className="space-y-2">
                  {PATHWAY_TRANSFORMATIONS.costa_rica.forTheWomanWho.map((item, i) => (
                    <li key={i} className="text-gray-700 flex items-start gap-2">
                      <span className="text-terracotta">...</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              
              <TransformationCard 
                before={PATHWAY_TRANSFORMATIONS.costa_rica.transformation.before}
                after={PATHWAY_TRANSFORMATIONS.costa_rica.transformation.after}
              />
              
              <TestimonialQuote testimonial={PATHWAY_TRANSFORMATIONS.costa_rica.testimonial} />
              
              {/* Room Options */}
              <div className="bg-sand/30 rounded-xl p-5 mb-6">
                <p className="font-medium text-forest mb-3">Choose Your Experience:</p>
                <div className="space-y-2">
                  <div className="flex justify-between items-center p-3 bg-white rounded-lg">
                    <span className="text-gray-700">Shared Room</span>
                    <span className="font-bold text-forest">$4,997</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-white rounded-lg">
                    <span className="text-gray-700">Private Room</span>
                    <span className="font-bold text-forest">$5,997</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-white rounded-lg">
                    <span className="text-gray-700">Suite</span>
                    <span className="font-bold text-forest">$7,497</span>
                  </div>
                </div>
              </div>
              
              <Button 
                className="btn-secondary h-14 px-8 text-lg"
                onClick={() => navigate('/costa-rica-retreat')}
                data-testid="reserve-costa-rica-btn"
              >
                Reserve Your Spot <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
            
            {/* Image */}
            <div className="order-1 lg:order-2">
              <img 
                src={PATHWAY_TRANSFORMATIONS.costa_rica.image}
                alt="Woman at peace in nature"
                className="rounded-2xl shadow-2xl w-full h-[450px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* SDC - VIP */}
      <section className="py-20 bg-forest text-white" data-testid="pathway-sdc">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge className="bg-terracotta text-white mb-4">VIP Experience</Badge>
            <h2 className="font-heading text-4xl md:text-5xl font-bold mb-4">
              Sister Diamonds Collective (SDC)
            </h2>
            <p className="text-2xl text-white/80">$25,000</p>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Image */}
            <div>
              <img 
                src={PATHWAY_TRANSFORMATIONS.sdc.image}
                alt="Women laughing together"
                className="rounded-2xl shadow-2xl w-full h-[400px] object-cover"
              />
            </div>
            
            {/* Content */}
            <div>
              <h3 className="font-heading text-3xl md:text-4xl mb-3">
                {PATHWAY_TRANSFORMATIONS.sdc.identity}
              </h3>
              <p className="text-xl text-white/80 mb-6">
                {PATHWAY_TRANSFORMATIONS.sdc.tagline}
              </p>
              
              <div className="mb-6">
                <p className="text-sm uppercase tracking-wide text-terracotta font-medium mb-3">This is for the woman who...</p>
                <ul className="space-y-2">
                  {PATHWAY_TRANSFORMATIONS.sdc.forTheWomanWho.map((item, i) => (
                    <li key={i} className="text-white/90 flex items-start gap-2">
                      <span className="text-terracotta">...</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              
              {/* SDC Transformation */}
              <div className="grid md:grid-cols-2 gap-4 my-6">
                <div className="bg-white/10 rounded-xl p-5">
                  <p className="text-xs uppercase tracking-wide text-white/60 mb-3 font-medium">Where you are now</p>
                  <ul className="space-y-2">
                    {PATHWAY_TRANSFORMATIONS.sdc.transformation.before.map((item, i) => (
                      <li key={i} className="text-white/70 text-sm flex items-start gap-2">
                        <span className="text-white/40 mt-0.5">—</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="bg-terracotta/30 rounded-xl p-5">
                  <p className="text-xs uppercase tracking-wide text-terracotta mb-3 font-medium">Who you become</p>
                  <ul className="space-y-2">
                    {PATHWAY_TRANSFORMATIONS.sdc.transformation.after.map((item, i) => (
                      <li key={i} className="text-white text-sm flex items-start gap-2 font-medium">
                        <Crown className="h-4 w-4 text-terracotta flex-shrink-0 mt-0.5" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              
              {/* SDC Testimonial */}
              <div className="bg-white/10 rounded-xl p-5 my-6 relative">
                <Quote className="h-8 w-8 text-terracotta/50 absolute top-4 left-4" />
                <p className="text-white/90 italic pl-8 mb-3">"{PATHWAY_TRANSFORMATIONS.sdc.testimonial.quote}"</p>
                <div className="pl-8">
                  <p className="font-medium text-white">{PATHWAY_TRANSFORMATIONS.sdc.testimonial.author}</p>
                  <p className="text-sm text-white/60">{PATHWAY_TRANSFORMATIONS.sdc.testimonial.role}</p>
                </div>
              </div>
              
              {/* SDC Highlights */}
              <div className="flex flex-wrap gap-3 mb-6">
                <span className="bg-white/20 px-4 py-2 rounded-full text-sm">Unlimited Coaching</span>
                <span className="bg-white/20 px-4 py-2 rounded-full text-sm">Exclusive Retreats</span>
                <span className="bg-white/20 px-4 py-2 rounded-full text-sm">Concierge Support</span>
                <span className="bg-white/20 px-4 py-2 rounded-full text-sm">Lifetime Sisterhood</span>
              </div>
              
              <Button 
                className="bg-terracotta hover:bg-terracotta/90 text-white h-14 px-8 text-lg"
                onClick={() => setSdcModalOpen(true)}
                data-testid="request-sdc-btn"
              >
                Request Invitation <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-cream">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-3xl font-bold text-forest text-center mb-12">
            Questions Before You Begin?
          </h2>
          
          <div className="space-y-4">
            {[
              {
                q: "I'm not sure which pathway is right for me.",
                a: "Start with Inner Circle ($97/mo). It's designed to help you experience our community and approach before committing to deeper work. Many of our most transformed women started right there."
              },
              {
                q: "Can I upgrade later?",
                a: "Absolutely. Your journey unfolds in divine timing. Many Sister Diamonds start with Inner Circle and later move into coaching or retreats when they're ready. We'll help you know when it's time."
              },
              {
                q: "What if I need more personalized support?",
                a: "Signature Coaching gives you 1:1 access to a coach matched specifically to your needs, plus sessions with Crystal and Dr. Nicole themselves. It's the most personalized transformation we offer outside of SDC."
              },
              {
                q: "Is SDC really worth $25,000?",
                a: "SDC members say the introductions, access, and unlimited support have transformed not just their lives—but their businesses, families, and legacies. It's for women who are ready to play at the highest level, surrounded by sisters doing the same."
              }
            ].map((faq, i) => (
              <Card key={i} className="card-base">
                <CardContent className="p-6">
                  <h3 className="font-heading text-lg font-semibold text-forest mb-2">{faq.q}</h3>
                  <p className="text-gray-600">{faq.a}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-sand/50">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-forest mb-4">
            The Woman You're Becoming is Waiting
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            Every pathway leads to the same place: a version of you that's been there all along, 
            waiting for permission to emerge. This is that permission.
          </p>
          <Button 
            className="btn-secondary h-14 px-10 text-lg"
            onClick={() => handleStartTrial('inner_circle')}
          >
            Start with Inner Circle <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      <Footer />
      
      <SubscriptionCheckout
        isOpen={checkoutOpen}
        onClose={() => setCheckoutOpen(false)}
        tier={selectedTier}
        tierInfo={tiers?.[selectedTier]}
      />

      {/* Inner Edge Application Modal */}
      <Dialog open={innerEdgeModalOpen} onOpenChange={setInnerEdgeModalOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-forest">Apply for Inner Edge</DialogTitle>
            <DialogDescription>
              Take the first step toward commanding every room you enter.
            </DialogDescription>
          </DialogHeader>
          
          {!showCalendly ? (
            <form onSubmit={handleInnerEdgeSubmit} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="ie-firstName">First Name *</Label>
                  <Input
                    id="ie-firstName"
                    required
                    value={innerEdgeForm.firstName}
                    onChange={(e) => setInnerEdgeForm({...innerEdgeForm, firstName: e.target.value})}
                    placeholder="Your first name"
                  />
                </div>
                <div>
                  <Label htmlFor="ie-lastName">Last Name *</Label>
                  <Input
                    id="ie-lastName"
                    required
                    value={innerEdgeForm.lastName}
                    onChange={(e) => setInnerEdgeForm({...innerEdgeForm, lastName: e.target.value})}
                    placeholder="Your last name"
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="ie-email">Email *</Label>
                <Input
                  id="ie-email"
                  type="email"
                  required
                  value={innerEdgeForm.email}
                  onChange={(e) => setInnerEdgeForm({...innerEdgeForm, email: e.target.value})}
                  placeholder="your@email.com"
                />
              </div>
              
              <div>
                <Label htmlFor="ie-phone">Phone Number *</Label>
                <Input
                  id="ie-phone"
                  type="tel"
                  required
                  value={innerEdgeForm.phone}
                  onChange={(e) => setInnerEdgeForm({...innerEdgeForm, phone: e.target.value})}
                  placeholder="(555) 123-4567"
                />
              </div>
              
              <div>
                <Label htmlFor="ie-referral">How did you hear about Inner Edge? *</Label>
                <Select 
                  value={innerEdgeForm.referralSource} 
                  onValueChange={(value) => setInnerEdgeForm({...innerEdgeForm, referralSource: value})}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select an option" />
                  </SelectTrigger>
                  <SelectContent>
                    {REFERRAL_SOURCES.map((source) => (
                      <SelectItem key={source} value={source}>{source}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {innerEdgeForm.referralSource === 'Other' && (
                <div>
                  <Label htmlFor="ie-referralOther">Please specify</Label>
                  <Input
                    id="ie-referralOther"
                    value={innerEdgeForm.referralOther}
                    onChange={(e) => setInnerEdgeForm({...innerEdgeForm, referralOther: e.target.value})}
                    placeholder="How did you find us?"
                  />
                </div>
              )}
              
              <Button type="submit" className="w-full btn-primary" disabled={submitting}>
                {submitting ? (
                  <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Submitting...</>
                ) : (
                  <>Continue to Schedule Call <Calendar className="ml-2 h-4 w-4" /></>
                )}
              </Button>
            </form>
          ) : (
            <div className="mt-4">
              <div className="bg-forest/5 rounded-lg p-4 mb-4">
                <p className="text-forest font-medium">Thank you, {innerEdgeForm.firstName}!</p>
                <p className="text-gray-600 text-sm">Schedule your discovery call below to discuss your Inner Edge journey.</p>
              </div>
              
              {/* Calendly Embed */}
              <div className="rounded-lg overflow-hidden border">
                <iframe
                  src="https://calendly.com/sisterdiamonds/inner-edge-discovery"
                  width="100%"
                  height="600"
                  frameBorder="0"
                  title="Schedule Inner Edge Discovery Call"
                />
              </div>
              
              <Button 
                variant="outline" 
                className="w-full mt-4"
                onClick={() => {
                  setInnerEdgeModalOpen(false);
                  setShowCalendly(false);
                  setInnerEdgeForm({
                    firstName: '',
                    lastName: '',
                    email: '',
                    phone: '',
                    referralSource: '',
                    referralOther: ''
                  });
                }}
              >
                Close
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* SDC Invitation Request Modal */}
      <Dialog open={sdcModalOpen} onOpenChange={setSdcModalOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-forest">Request SDC Invitation</DialogTitle>
            <DialogDescription>
              Join the most exclusive sisterhood of powerhouse women. Crystal and Dr. Nicole personally review each request.
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSdcSubmit} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="sdc-firstName">First Name *</Label>
                <Input
                  id="sdc-firstName"
                  required
                  value={sdcForm.firstName}
                  onChange={(e) => setSdcForm({...sdcForm, firstName: e.target.value})}
                  placeholder="Your first name"
                />
              </div>
              <div>
                <Label htmlFor="sdc-lastName">Last Name *</Label>
                <Input
                  id="sdc-lastName"
                  required
                  value={sdcForm.lastName}
                  onChange={(e) => setSdcForm({...sdcForm, lastName: e.target.value})}
                  placeholder="Your last name"
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="sdc-email">Email *</Label>
              <Input
                id="sdc-email"
                type="email"
                required
                value={sdcForm.email}
                onChange={(e) => setSdcForm({...sdcForm, email: e.target.value})}
                placeholder="your@email.com"
              />
            </div>
            
            <div>
              <Label htmlFor="sdc-phone">Phone Number *</Label>
              <Input
                id="sdc-phone"
                type="tel"
                required
                value={sdcForm.phone}
                onChange={(e) => setSdcForm({...sdcForm, phone: e.target.value})}
                placeholder="(555) 123-4567"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="sdc-company">Company/Organization</Label>
                <Input
                  id="sdc-company"
                  value={sdcForm.company}
                  onChange={(e) => setSdcForm({...sdcForm, company: e.target.value})}
                  placeholder="Your company"
                />
              </div>
              <div>
                <Label htmlFor="sdc-title">Title/Role</Label>
                <Input
                  id="sdc-title"
                  value={sdcForm.title}
                  onChange={(e) => setSdcForm({...sdcForm, title: e.target.value})}
                  placeholder="Your role"
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="sdc-interest">What draws you to SDC? *</Label>
              <Textarea
                id="sdc-interest"
                required
                value={sdcForm.interest}
                onChange={(e) => setSdcForm({...sdcForm, interest: e.target.value})}
                placeholder="Tell us what excites you about joining this community..."
                rows={3}
              />
            </div>
            
            <div>
              <Label htmlFor="sdc-goals">What do you hope to accomplish in the next year? *</Label>
              <Textarea
                id="sdc-goals"
                required
                value={sdcForm.goals}
                onChange={(e) => setSdcForm({...sdcForm, goals: e.target.value})}
                placeholder="Share your vision and goals..."
                rows={3}
              />
            </div>
            
            <Button type="submit" className="w-full btn-primary" disabled={submitting}>
              {submitting ? (
                <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Sending Request...</>
              ) : (
                <>Submit Invitation Request <ArrowRight className="ml-2 h-4 w-4" /></>
              )}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Signature Coaching Interest Modal */}
      <Dialog open={coachingModalOpen} onOpenChange={setCoachingModalOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-forest">Begin Your Coaching Journey</DialogTitle>
            <DialogDescription>
              Share a bit about yourself so we can match you with the perfect coach for your transformation.
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleCoachingSubmit} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="coach-firstName">First Name *</Label>
                <Input
                  id="coach-firstName"
                  required
                  value={coachingForm.firstName}
                  onChange={(e) => setCoachingForm({...coachingForm, firstName: e.target.value})}
                  placeholder="Your first name"
                />
              </div>
              <div>
                <Label htmlFor="coach-lastName">Last Name *</Label>
                <Input
                  id="coach-lastName"
                  required
                  value={coachingForm.lastName}
                  onChange={(e) => setCoachingForm({...coachingForm, lastName: e.target.value})}
                  placeholder="Your last name"
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="coach-email">Email *</Label>
              <Input
                id="coach-email"
                type="email"
                required
                value={coachingForm.email}
                onChange={(e) => setCoachingForm({...coachingForm, email: e.target.value})}
                placeholder="your@email.com"
              />
            </div>
            
            <div>
              <Label htmlFor="coach-phone">Phone Number *</Label>
              <Input
                id="coach-phone"
                type="tel"
                required
                value={coachingForm.phone}
                onChange={(e) => setCoachingForm({...coachingForm, phone: e.target.value})}
                placeholder="(555) 123-4567"
              />
            </div>
            
            <div>
              <Label htmlFor="coach-challenge">What's your biggest challenge right now? *</Label>
              <Textarea
                id="coach-challenge"
                required
                value={coachingForm.currentChallenge}
                onChange={(e) => setCoachingForm({...coachingForm, currentChallenge: e.target.value})}
                placeholder="Share what's weighing on you or holding you back..."
                rows={3}
              />
            </div>
            
            <div>
              <Label htmlFor="coach-outcome">What outcome are you hoping for? *</Label>
              <Textarea
                id="coach-outcome"
                required
                value={coachingForm.desiredOutcome}
                onChange={(e) => setCoachingForm({...coachingForm, desiredOutcome: e.target.value})}
                placeholder="Describe your ideal transformation..."
                rows={3}
              />
            </div>
            
            <div>
              <Label htmlFor="coach-timeline">When are you hoping to start? *</Label>
              <Select 
                value={coachingForm.timeline} 
                onValueChange={(value) => setCoachingForm({...coachingForm, timeline: value})}
                required
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select timing" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="immediately">As soon as possible</SelectItem>
                  <SelectItem value="1-2 weeks">Within 1-2 weeks</SelectItem>
                  <SelectItem value="1 month">Within a month</SelectItem>
                  <SelectItem value="exploring">Just exploring options</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <Button type="submit" className="w-full btn-primary" disabled={submitting}>
              {submitting ? (
                <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Submitting...</>
              ) : (
                <>Submit Interest <ArrowRight className="ml-2 h-4 w-4" /></>
              )}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PathwaysPage;
